import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../apiContext';
import { ApiError } from '../api';
import type { WikiListItem } from '../types';

function wikiUrl(w: WikiListItem) {
  return `/wiki/${encodeURIComponent(w.projectName)}/${encodeURIComponent(w.wikiId)}`;
}

function relative(iso: string | null) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  return d === 1 ? 'yesterday' : `${d} days ago`;
}

function WikiCard({ w, onOpen, onStar }: { w: WikiListItem; onOpen: () => void; onStar: () => void }) {
  return (
    <div className="wiki-card">
      <button className="wiki-card-main" onClick={onOpen}>
        <span className="wiki-card-name">{w.wikiName.replace(/\.wiki$/, '')}</span>
        <span className="wiki-card-meta">
          {w.projectName} · {w.type === 'codeWiki' ? 'code wiki' : 'project wiki'}
          {w.lastOpened ? ` · opened ${relative(w.lastOpened)}` : ''}
        </span>
      </button>
      <button className={`star${w.favorite ? ' is-on' : ''}`} title={w.favorite ? 'Remove favorite' : 'Add favorite'} onClick={onStar} aria-label="Toggle favorite">
        {w.favorite ? '★' : '☆'}
      </button>
    </div>
  );
}

export default function WikiPicker() {
  const api = useApi();
  const navigate = useNavigate();
  const [wikis, setWikis] = useState<WikiListItem[] | null>(null);
  const [error, setError] = useState<{ code: string; detail?: string } | null>(null);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    api
      .listWikis()
      .then(setWikis)
      .catch(e => setError(e instanceof ApiError ? { code: e.code, detail: e.detail } : { code: 'error', detail: String(e) }));
  }, [api]);

  const open = (w: WikiListItem) => {
    api.openWiki(w.projectName, w.wikiId).catch(() => undefined);
    navigate(wikiUrl(w));
  };

  const star = (w: WikiListItem) => {
    const next = !w.favorite;
    setWikis(ws => ws?.map(x => (x.wikiId === w.wikiId ? { ...x, favorite: next } : x)) ?? null);
    api.setFavorite(w.projectName, w.wikiId, next).catch(() => setWikis(ws => ws?.map(x => (x.wikiId === w.wikiId ? { ...x, favorite: !next } : x)) ?? null));
  };

  const filtered = useMemo(() => {
    if (!wikis) return [];
    const q = filter.trim().toLowerCase();
    return q ? wikis.filter(w => `${w.projectName} ${w.wikiName}`.toLowerCase().includes(q)) : wikis;
  }, [wikis, filter]);

  const favorites = filtered.filter(w => w.favorite);
  const recent = filtered.filter(w => w.lastOpened).sort((a, b) => (b.lastOpened! > a.lastOpened! ? 1 : -1)).slice(0, 5);
  const mostUsed = filtered.filter(w => w.openCount > 0).sort((a, b) => b.openCount - a.openCount).slice(0, 5);

  const byProject = useMemo(() => {
    const m = new Map<string, WikiListItem[]>();
    for (const w of filtered) {
      if (!m.has(w.projectName)) m.set(w.projectName, []);
      m.get(w.projectName)!.push(w);
    }
    return Array.from(m.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  if (error) {
    return (
      <main className="picker">
        <div className="notice notice-error">
          {error.code === 'consent_required' && (
            <>
              <strong>Azure DevOps access needs admin consent.</strong> An administrator must grant the app the “Azure DevOps / user_impersonation” permission in Entra. Details: {error.detail}
            </>
          )}
          {error.code === 'ado_forbidden' && (
            <>
              <strong>No Azure DevOps access.</strong> Your account signed in, but Azure DevOps rejected it. Make sure you have an active user in the {`organization`} with at least Basic access.
            </>
          )}
          {error.code !== 'consent_required' && error.code !== 'ado_forbidden' && (
            <>
              <strong>Could not load wikis.</strong> {error.detail ?? error.code}
            </>
          )}
        </div>
      </main>
    );
  }

  return (
    <main className="picker">
      <div className="picker-head">
        <h1>Open a wiki</h1>
        <input className="input" placeholder="Filter by project or wiki…" value={filter} onChange={e => setFilter(e.target.value)} autoFocus />
      </div>

      {wikis === null && <div className="muted">Loading wikis from Azure DevOps…</div>}

      {wikis && wikis.length === 0 && <div className="muted">No wikis visible to your account.</div>}

      {(favorites.length > 0 || recent.length > 0 || mostUsed.length > 0) && (
        <section className="picker-quick">
          {favorites.length > 0 && (
            <div className="picker-group">
              <h2>Favorites</h2>
              {favorites.map(w => (
                <WikiCard key={w.wikiId} w={w} onOpen={() => open(w)} onStar={() => star(w)} />
              ))}
            </div>
          )}
          {recent.length > 0 && (
            <div className="picker-group">
              <h2>Recently opened</h2>
              {recent.map(w => (
                <WikiCard key={w.wikiId} w={w} onOpen={() => open(w)} onStar={() => star(w)} />
              ))}
            </div>
          )}
          {mostUsed.length > 0 && (
            <div className="picker-group">
              <h2>Most used</h2>
              {mostUsed.map(w => (
                <WikiCard key={w.wikiId} w={w} onOpen={() => open(w)} onStar={() => star(w)} />
              ))}
            </div>
          )}
        </section>
      )}

      {byProject.length > 0 && (
        <section>
          <h2 className="section-title">All wikis by project</h2>
          <div className="picker-projects">
            {byProject.map(([project, items]) => (
              <div className="picker-group" key={project}>
                <h3>{project}</h3>
                {items.map(w => (
                  <WikiCard key={w.wikiId} w={w} onOpen={() => open(w)} onStar={() => star(w)} />
                ))}
              </div>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
